import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormInputWrapper from '../form-input-wrapper/FormInputWrapper.jsx';

import {
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
} from '../redux/form/form.actions';

import { uuidv4, filterArrayByProperty } from '../_global/js/form.utils';

const FormSelect = ({
  groupId,
  inputLabel,
  inputDescriptionText,
  parentElementClass,
  inputName,
  inputRequired,
  inputOptions,
  sectionLabel,
  inputValidationLabel,
  inputValidationMessage,
  conditionalInputs,
  dependentInputs,
  additionalInputs,
  preventProgression,
  inputTestData,
  autoPopulate,
  triggerValidation,
  formStepValidation,
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
  }) => {
  const inputId = useRef( uuidv4() );
  const [ inputValue, setInputValue ] = useState("");
  const [ showAdditional, setShowAdditional ] = useState(false);
  const [ isValid, setIsValid ] = useState(true);
  const [ requiresFormValidation, setRequiresFormValidation ] = useState(false);
  const [ conditionalQuestions, setConditionalQuestions ] = useState(null);
  const validationLabel = inputValidationLabel || inputLabel;
  const errorMessage = inputValidationMessage || 'This field is required.';

  /**
   * When the component has been mounted
   */
  useEffect(() => {
    let nonMatchingDependentFieldReferencesArray = [];

    // Mark this field as required, as necessary
    if (inputRequired) {
      setRequiresFormValidation(true);
    }

    // Automatically hide any dependent inputs
    if (dependentInputs !== undefined && dependentInputs !== null) {
      // Capture all the input names
      for (let i = 0, j = dependentInputs.length; i < j; ++i) {
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputs[i].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }

    // Cleanup the component (unmount)
    return () => {
      // Remove this field from the invalid list
      removeValidationField(inputId.current);

      // Remove this field from the list preventing the form submission (if applicable)
      removePreventFormSubmissionField(inputId.current);

      // Reset any dependent questions
      // resetDependentQuestions(dependentInputs);
    };
  }, []);

  /**
   * When the field validation requirement has changed
   */
  useEffect(() => {
    if (requiresFormValidation) {
      // Add this field to the form validation state
      addValidationField({
        id: inputId.current,
        label: validationLabel,
        sectionLabel: sectionLabel,
        required: inputRequired,
        formStep: formStepValidation
      });
    } else {
      // Remove this field from the form validation state
      removeValidationField(inputId.current);
    }
  }, [requiresFormValidation]);

  /**
   * When the component has been instructed to validate it's input
   */
  useEffect(() => {
    if (triggerValidation !== null) {

      // Check if this input is in the current form step
      if (triggerValidation.formStep === formStepValidation) {
        // Check if the input is valid
        const validationSuccess = inputIsValid(inputValue);

        // Field is required, but failed validation
        if (inputRequired && !validationSuccess) {
          // Display the error message
          setIsValid(false);

          // Mark this field as invalid
          setRequiresFormValidation(true);
        }
      }
    }
  }, [triggerValidation]);

  /**
   * When the component has been instructed to auto-populate content
   */
  useEffect(() => {
    // Populate the field with test data
    if (autoPopulate && inputTestData) {
      setInputValue(inputTestData);
      setIsValid(true);
      setRequiresFormValidation(false);
    }
  }, [autoPopulate]);

  /**
   * When the value of the input has changed
   */
  useEffect(() => {
    let conditionalFieldsArray = null;
    let conditionalFieldsData = null;
    let matchingDependentFieldsArray = null;
    let nonMatchingDependentFieldsArray = null;
    let nonMatchingDependentFieldReferencesArray = [];
    let preventFormSubmission = false;

    /**
     * Check if the user has entered a value and if there are
     * further actions to take. e.g. additional fields or checks.
     */
    if (inputIsValid(inputValue)) {

      if (conditionalInputs) {
        // If conditional inputs have been provided, check for any matches
        conditionalFieldsArray = filterArrayByProperty(conditionalInputs, 'responseMatch', inputValue);

        if (conditionalFieldsArray.length !== 0) {
          // Assign an ID to each input
          conditionalFieldsData = conditionalFieldsArray[0].inputs;

          for (let i = 0, j = conditionalFieldsData.length; i < j; ++i) {
            conditionalFieldsData[i].id = uuidv4();
          }

          setConditionalQuestions(conditionalFieldsData);
        } else {
          setConditionalQuestions(null);
        }
      }

      if (dependentInputs !== undefined && dependentInputs !== null) {
        // If dependent inputs have been provided, update the ignore list as necessary
        matchingDependentFieldsArray = filterArrayByProperty(dependentInputs, 'responseMatch', inputValue);
        nonMatchingDependentFieldsArray = filterArrayByProperty(dependentInputs, 'responseMatch', inputValue, 0, true);

        /**
         * Note that we hide fields BEFORE showing any.
         * This order is important so that the same fields can appear in
         * different lists of dependent fields.
         * That is, if the same input appears in multiple responseMatch
         * checks, we want to make sure it's still visible after the
         * update to the state.
         */
        if (nonMatchingDependentFieldsArray.length !== 0) {
          // One or more dependents should be hidden
          // Capture all the input names
          for (let i = 0, j = nonMatchingDependentFieldsArray.length; i < j; ++i) {
            nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(nonMatchingDependentFieldsArray[i].inputs);
          }

          addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
        }

        if (matchingDependentFieldsArray.length !== 0) {
          // One or more dependents can be shown
          removeDependentsFromIgnoreList(matchingDependentFieldsArray[0].inputs);
        }
      }

      // Check if the response should prevent the form progress and submission
      if (preventProgression !== undefined && preventProgression !== null) {
        // Check for matching values
        for (let i = 0, j = preventProgression.length; i < j; ++i) {
          if (inputValue === preventProgression[i]) {
            // A match was found
            preventFormSubmission = true;
            break;
          }
        }

        if (preventFormSubmission) {
          // Prevent the form submission
          addPreventFormSubmissionField(inputId.current);
        } else {
          // Re-enable form submission
          removePreventFormSubmissionField(inputId.current);
        }
      }
    } else {
      // Reset any conditional questions
      if (conditionalQuestions !== null) {
        setConditionalQuestions(null);
      }

      // Reset any dependent questions
      resetDependentQuestions(dependentInputs);

      // Re-enable form submission
      removePreventFormSubmissionField(inputId.current);
    }
  }, [inputValue]);

  /**
   * Respond to changes to the select field
   *
   * @param {Change Event} evt Event that triggered the input to update
   *
   * @returns void
   */
  const onInputChange = (evt) => {
    const fieldValue = evt.target.value;

    // Check if the input is valid
    const validationSuccess = inputIsValid(fieldValue);

    // Capture the inputted value
    setInputValue(fieldValue);

    // Field is required, but failed validation
    if (inputRequired && !validationSuccess) {
      // Display the error message
      setIsValid(false);

      // Mark this field as invalid
      setRequiresFormValidation(true);
    }

    // Field passed validation
    if (validationSuccess) {
      // Update the validation message
      setIsValid(true);

      // Remove this field from the invalid list
      setRequiresFormValidation(false);
    }
  };

  const onAdditionalButtonClick = (evt) => {
    evt.preventDefault();

    if (!showAdditional) {
      setShowAdditional(true);
    } else {
      setShowAdditional(false);
    }
  };

  /**
   * Validate the field's value
   *
   * @param {String} valueToCheck The value of the input field
   *
   * @returns Boolean
   */
  function inputIsValid(valueToCheck) {
    let isValid = false;

    if (valueToCheck !== '') {
      isValid = true;
    }

    return isValid;
  }

  /**
   * Capture the input names of all questions that are
   * dependnent on the response of this question and
   * add them global ignore list
   *
   * @param {Array} dependentInputsConfiguration The entire dependent questions configuration
   *
   * @returns void
   */
  function resetDependentQuestions(dependentInputsConfiguration) {
    let nonMatchingDependentFieldReferencesArray = [];

    if (dependentInputsConfiguration !== undefined && dependentInputsConfiguration !== null) {
      // Loop over the possible dependent questions
      for (let k = 0, b = dependentInputsConfiguration.length; k < b; ++k) {
        // Capture all dependent inputs
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputsConfiguration[k].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }
  }

  return (
    <div className={parentElementClass}>
      <div>
        <label htmlFor={inputId.current} className="qld__label">{ (inputRequired) ? <abbr title="required">*</abbr> : null }{inputLabel}</label>
        {
          (inputDescriptionText !== undefined && inputDescriptionText !== null) ? <span id={`${inputId.current}-description`} className="qld__hint-text">{inputDescriptionText}</span> : null
        }
        {
          (!isValid) ?
            <p className="sq-form-error">
              {errorMessage}
            </p>
          : null
        }
        <select
          id={inputId.current}
          className={!isValid ? "qld__text-input--error" : null}
          name={inputName}
          value={inputValue}
          onChange={onInputChange}
          required={inputRequired}
          autoComplete="off"
          data-groupid={groupId}
          data-validation-label={validationLabel}
          data-section-label={sectionLabel}
        >
        {
          inputOptions.map( option => {
            return (
              <option
                key={option.value}
                value={option.value}
                >
                {option.label}
              </option>
            );
          })
        }
        </select>
      </div>
      {
        (conditionalQuestions) ?
        <div>
          {
            conditionalQuestions.map( (formInput) => {
              const formInputConfig = { ...formInput };

              /**
               * If provided, add the section legend (label) to the input configuration.
               * This is so we can include the section label in the form validation messages and summary.
               */
              if (sectionLabel) {
                formInputConfig.sectionLabel = sectionLabel;
              }

              return (
                <FormInputWrapper
                  key={formInputConfig.id}
                  inputConfiguration={formInputConfig}
                  autoPopulate={autoPopulate}
                  triggerValidation={triggerValidation}
                  formStepValidation={formStepValidation}
                />
              );
            })
          }
          </div>
        : null
      }
      {
        (additionalInputs && !showAdditional) ? <button onClick={onAdditionalButtonClick} className="qld__btn">{additionalInputs.buttonLabel}</button> : null
      }
      {
        (showAdditional) ?
          <div>
            <div>
              <button onClick={onAdditionalButtonClick} className="qld__btn qld__btn--secondary">Remove this section</button>
            </div>
            {
              additionalInputs.inputs.map( (formInput, index) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={index}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
    </div>
  );
};

FormSelect.propTypes = {
  "groupId": PropTypes.string,
  "inputLabel": PropTypes.string.isRequired,
  "inputDescriptionText": PropTypes.string,
  "parentElementClass": PropTypes.string,
  "inputName": PropTypes.string.isRequired,
  "inputRequired": PropTypes.bool.isRequired,
  "inputOptions": PropTypes.array.isRequired,
  "sectionLabel": PropTypes.string,
  "inputValidationLabel": PropTypes.string,
  "inputValidationMessage": PropTypes.string,
  "conditionalInputs": PropTypes.array,
  "dependentInputs": PropTypes.array,
  "additionalInputs": PropTypes.object,
  "preventProgression": PropTypes.array,
  "inputTestData": PropTypes.string,
  "autoPopulate": PropTypes.bool,
  "triggerValidation": PropTypes.object,
  "formStepValidation": PropTypes.number.isRequired,
  "addValidationField":  PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "removeValidationField":  PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "addDependentsToIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removeDependentsFromIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "addPreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removePreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] )
};

const mapDispatchToProps = (dispatch) => {
  return {
    "addValidationField": formFieldData => dispatch(addValidationField(formFieldData)),
    "removeValidationField": formFieldData => dispatch(removeValidationField(formFieldData)),
    "addDependentsToIgnoreList": inputReferences => dispatch(addDependentsToIgnoreList(inputReferences)),
    "removeDependentsFromIgnoreList": inputReferences => dispatch(removeDependentsFromIgnoreList(inputReferences)),
    "addPreventFormSubmissionField": inputNames => dispatch(addPreventFormSubmissionField(inputNames)),
    "removePreventFormSubmissionField": inputNames => dispatch(removePreventFormSubmissionField(inputNames))
  };
};

export default connect(null, mapDispatchToProps)(FormSelect);